import { useMemo, useState } from 'react';

type Charges = {
  total?: {
    amount?: string | number;
  };
};

type LeadInOffer = {
  packageOffers?: {
    [key: string]: Charges;
  };
};

type Offer = {
  charges?: Charges;
};

export type RoomOption = {
  offers?: Offer[];
  leadInOffers?: LeadInOffer[];
};

const hasValidOffer = (
  roomOption?: RoomOption,
  route: string = '',
  isPreSearch: boolean = true,
) => {
  if (isPreSearch) {
    return (roomOption?.leadInOffers ?? []).some(
      (r) => r?.packageOffers?.[route]?.total?.amount,
    );
  }

  // Post-Search
  return (
    roomOption &&
    Array.isArray(roomOption?.offers) &&
    roomOption?.offers?.length > 0
  );
};

const offerPrice = (offer?: Offer) =>
  offer?.charges?.total?.amount
    ? Number(offer.charges.total.amount)
    : Number.MAX_SAFE_INTEGER;

type UseOtherRoomOptionsProps<T> = {
  roomOptions: T[] | null | undefined;
  route?: string;
  isPreSearch?: boolean;
};

const useOtherRoomOptions = <T,>({
  roomOptions,
  route,
  isPreSearch,
}: UseOtherRoomOptionsProps<T>) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [_, ...hiddenRooms] = roomOptions || [];
  const hiddenRoomCount = hiddenRooms?.length;
  const toggleVisibleRooms = () => setIsExpanded((val) => !val);

  const orderedRooms = useMemo(() => {
    if (
      !roomOptions ||
      (Array.isArray(roomOptions) && roomOptions.length === 0)
    )
      return [];

    const { roomsWithOffers, roomsWithoutOffers } = roomOptions.reduce(
      (accum, room) => {
        const roomHasValidOffer = hasValidOffer(
          room as RoomOption,
          route,
          isPreSearch,
        );

        if (roomHasValidOffer) {
          accum.roomsWithOffers.push(room);
        } else {
          accum.roomsWithoutOffers.push(room);
        }

        return accum;
      },
      {
        roomsWithOffers: [] as T[],
        roomsWithoutOffers: [] as T[],
      },
    );

    const sortedRoomsWithOffers = roomsWithOffers.sort((a, b) => {
      // Type coercing as this is a shared hook and we want to allow for a generic RoomOption type
      // to be used, as Qantas and Jetstar have different RoomOption types, and we stil want
      // to have typed utils.
      const roomA = a as RoomOption;
      const roomB = b as RoomOption;
      if (isPreSearch) {
        // On load, route can be 'undefined<destinationCode>', eg. 'undefinedMEL'
        if (!route || route.startsWith('undefined')) return 0;

        return (
          offerPrice({
            charges: roomA?.leadInOffers?.[0]?.packageOffers?.[route],
          }) -
          offerPrice({
            charges: roomB?.leadInOffers?.[0]?.packageOffers?.[route],
          })
        );
      }

      // Post-search
      return offerPrice(roomA?.offers?.[0]) - offerPrice(roomB?.offers?.[0]);
    });

    return [...sortedRoomsWithOffers, ...roomsWithoutOffers];
  }, [roomOptions, route, isPreSearch]);

  return {
    visibleRooms: orderedRooms,
    toggleVisibleRooms,
    hiddenRoomCount,
    isExpanded,
  };
};

export default useOtherRoomOptions;
